import { useEffect, useRef, useState } from 'react';
import uPlot, { type AlignedData, type Options } from 'uplot';
import { fmtNumber } from '../lib/format';

export interface ChartSeries {
  label: string;
  unit: string;
  /** Epoch ms */
  t: number[];
  v: (number | null)[];
  /** Value labels for states ("0": "Manual"); drawn as steps. */
  labels: Record<string, string> | null;
}

interface Panel {
  key: string;
  unit: string;
  series: ChartSeries[];
  step: boolean;
  labels: Record<string, string> | null;
}

/** One panel per unit; each state with labels gets its own short step panel. */
export function panelsFor(series: ChartSeries[]): Panel[] {
  const panels: Panel[] = [];
  for (const s of series) {
    if (s.labels) {
      panels.push({ key: `state:${s.label}`, unit: '', series: [s], step: true, labels: s.labels });
      continue;
    }
    const existing = panels.find((p) => !p.step && p.unit === s.unit);
    if (existing) existing.series.push(s);
    else panels.push({ key: `unit:${s.unit}`, unit: s.unit, series: [s], step: false, labels: null });
  }
  return panels;
}

const css = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
const seriesColor = (i: number) => css(`--series-${(i % 8) + 1}`);

/**
 * Real gaps (the plant was offline, nothing stored) become null so the line breaks there:
 * a gap is more than 5 times the series' usual interval.
 */
function withGaps(s: ChartSeries): [number[], (number | null)[]] {
  const t: number[] = [];
  const v: (number | null)[] = [];
  const steps = s.t.slice(1).map((x, i) => x - s.t[i]!).sort((a, b) => a - b);
  const typical = steps[Math.floor(steps.length / 2)] ?? 0;
  for (let i = 0; i < s.t.length; i++) {
    if (i > 0 && typical > 0 && s.t[i]! - s.t[i - 1]! > typical * 5) {
      t.push((s.t[i - 1]! + s.t[i]!) / 2000);
      v.push(null);
    }
    t.push(s.t[i]! / 1000);
    v.push(s.v[i] ?? null);
  }
  return [t, v];
}

/**
 * Series share one x array in uPlot. join() fills the holes between series with undefined,
 * which uPlot steps over; the nulls from withGaps() break the line.
 */
function align(series: ChartSeries[]): AlignedData {
  return uPlot.join(series.map(withGaps) as unknown as AlignedData[]);
}

/** 24-hour ticks in the plant's zone; dates once the range spans days. */
function timeTicks(timezone: string) {
  const time = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const date = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, day: 'numeric', month: 'short' });
  const year = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, month: 'short', year: 'numeric' });
  return (_u: uPlot, splits: number[]) => {
    const span = (splits[splits.length - 1] ?? 0) - (splits[0] ?? 0);
    return splits.map((s) => {
      const ms = s * 1000;
      if (span > 200 * 86400) return year.format(ms);
      if (span > 2 * 86400) return date.format(ms);
      const hm = time.format(ms);
      return hm === '00:00' ? date.format(ms) : hm;
    });
  };
}

export function TimeSeriesChart({
  series,
  from,
  to,
  timezone,
  onZoom,
}: {
  series: ChartSeries[];
  from: number;
  to: number;
  timezone: string;
  onZoom: (from: number, to: number) => void;
}) {
  const [theme, setTheme] = useState(0);
  useEffect(() => {
    const bump = () => setTheme((n) => n + 1);
    window.addEventListener('vardex-theme', bump);
    return () => window.removeEventListener('vardex-theme', bump);
  }, []);
  const panels = panelsFor(series);
  let colorIndex = 0;
  return (
    <div className="space-y-2">
      {panels.map((p) => {
        const first = colorIndex;
        colorIndex += p.series.length;
        return (
          <PlotPanel key={`${p.key}:${theme}`} panel={p} from={from} to={to} timezone={timezone} onZoom={onZoom} colorOffset={first} />
        );
      })}
    </div>
  );
}

function PlotPanel({
  panel, from, to, timezone, onZoom, colorOffset,
}: { panel: Panel; from: number; to: number; timezone: string; onZoom: (from: number, to: number) => void; colorOffset: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const plot = useRef<uPlot | null>(null);
  const zoom = useRef(onZoom);
  zoom.current = onZoom;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const muted = css('--muted');
    const rule = css('--rule');
    const labels = panel.labels;
    const height = panel.step ? 110 : 260;
    const fmt = (v: number | null) => (v === null || v === undefined ? '–' : labels ? (labels[String(v)] ?? fmtNumber(v)) : fmtNumber(v));

    const opts: Options = {
      width: el.clientWidth,
      height,
      tzDate: (ts) => uPlot.tzDate(new Date(ts * 1000), timezone),
      scales: {
        x: { time: true, min: from / 1000, max: to / 1000 },
        y: labels
          ? { range: () => { const keys = Object.keys(labels).map(Number); return [Math.min(...keys) - 0.2, Math.max(...keys) + 0.2]; } }
          : {},
      },
      cursor: { sync: { key: 'vardex-chart' }, drag: { x: true, y: false } },
      legend: { live: true },
      axes: [
        { stroke: muted, grid: { stroke: rule, width: 1 }, ticks: { stroke: rule }, values: timeTicks(timezone), space: 60 },
        {
          stroke: muted,
          grid: { stroke: rule, width: 1 },
          ticks: { stroke: rule },
          size: labels ? 110 : 60,
          label: panel.unit || undefined,
          labelFont: '12px system-ui',
          ...(labels && { splits: () => Object.keys(labels).map(Number), values: (_u: uPlot, vals: number[]) => vals.map((v) => labels[String(v)] ?? '') }),
          ...(!labels && { values: (_u: uPlot, vals: number[]) => vals.map((v) => fmtNumber(v)) }),
        },
      ],
      series: [
        { value: (_u, ts) => (ts ? new Intl.DateTimeFormat('en-GB', { timeZone: timezone, dateStyle: 'medium', timeStyle: 'medium' }).format(ts * 1000) : '–') },
        ...panel.series.map((s, i) => ({
          label: s.label + (s.unit && !panel.unit ? ` (${s.unit})` : ''),
          stroke: seriesColor(colorOffset + i),
          width: 1.6,
          spanGaps: false,
          points: { show: false },
          value: (_u: uPlot, v: number | null) => fmt(v) + (v !== null && s.unit && !labels ? ` ${s.unit}` : ''),
          ...(panel.step && { paths: uPlot.paths.stepped!({ align: 1 }) }),
        })),
      ],
      hooks: {
        setSelect: [
          (u) => {
            if (u.select.width < 8) return;
            const a = u.posToVal(u.select.left, 'x') * 1000;
            const b = u.posToVal(u.select.left + u.select.width, 'x') * 1000;
            u.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false);
            zoom.current(Math.round(a), Math.round(b));
          },
        ],
      },
    };

    const u = new uPlot(opts, align(panel.series), el);
    plot.current = u;
    const ro = new ResizeObserver(() => u.setSize({ width: el.clientWidth, height }));
    ro.observe(el);
    return () => {
      ro.disconnect();
      u.destroy();
      plot.current = null;
    };
    // Re-create when the shape changes; data-only changes go through setData below.
  }, [panel.key, panel.series.length, timezone, colorOffset]);

  useEffect(() => {
    const u = plot.current;
    if (!u) return;
    u.setData(align(panel.series), false);
    u.setScale('x', { min: from / 1000, max: to / 1000 });
  }, [panel, from, to]);

  return <div ref={ref} className="w-full" />;
}

/** CSV of what the chart shows, times in UTC (ISO 8601) and in the plant's zone. */
export function toCsv(series: ChartSeries[], timezone: string): string {
  const data = align(series);
  const zoned = new Intl.DateTimeFormat('sv-SE', { timeZone: timezone, dateStyle: 'short', timeStyle: 'medium' });
  const header = ['time_utc', `time_${timezone.replace(/\//g, '_')}`, ...series.map((s) => `${s.label}${s.unit ? ` (${s.unit})` : ''}`)];
  const quote = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const lines = [header.map(quote).join(',')];
  const xs = data[0] as number[];
  for (let i = 0; i < xs.length; i++) {
    const ms = xs[i]! * 1000;
    lines.push([new Date(ms).toISOString(), zoned.format(ms), ...series.map((_, j) => String((data[j + 1] as (number | null)[])[i] ?? ''))].join(','));
  }
  return `${lines.join('\n')}\n`;
}
